import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ApiGetAllPaginatedResponse, ApiGetAllResponse } from '../interfaces/api-get-all-response.interface';
import { AbstractApiService, Filter, Pagination, QueryParams, Sort } from './abstract-api.service';
import { ApiCreateSingleResponse } from '../interfaces/api-create-single-response.interface';
import { catchError, map } from 'rxjs/operators';
import { UserReadableApiV2Error } from '../errors/user-readable-api-v2.error';
import { BackendErrorTranslatorService } from '../../services/localization/backend-error-translator.service';

interface GetAllPaginatedResponse<ItemType> {
    page: number;
    page_count: number;
    total_items: number;
    items: ItemType[];
}

@Injectable()
export abstract class AbstractApiV2Service<ItemType> extends AbstractApiService<ItemType> {
    /** stand-alone filters
     * remove any prefix or suffix when setting query params
     */
    public customFilters: string[];
    /**
     * Use this to load source data with filters without adding them manually from table
     */
    public staticFilters: { key: string; value: string }[] = [];
    public versionPrefix = '/v2';

    protected abstract resourceName: string;

    protected constructor(protected readonly http: HttpClient, private readonly backendErrorTranslator: BackendErrorTranslatorService) {
        super();
    }

    public convertToUserReadableError(httpError: HttpErrorResponse): Observable<any> {
        return throwError(() => new UserReadableApiV2Error(this.backendErrorTranslator, httpError));
    }

    public getAll(filter: Filter = {}, queryParams: QueryParams = {}): Observable<ApiGetAllResponse<ItemType>> {
        let httpParams: HttpParams = new HttpParams();

        for (const filterKey of Object.keys(filter)) {
            const filterValue = filter[filterKey];

            if (filterValue === undefined || filterValue === null || filterValue === '') {
                continue;
            }

            httpParams = httpParams.set(`filter_by_${filterKey}`, filterValue);
        }

        for (const paramName of Object.keys(queryParams)) {
            const paramValue = queryParams[paramName];
            httpParams = httpParams.set(paramName, paramValue);
        }

        return this.http
            .get<ApiGetAllResponse<ItemType>>(this.getLocation(), {
                params: httpParams,
            })
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public getAllPaginated(
        pagination: Pagination = { page: 1 },
        filter: Filter = {},
        sort: Sort | null = null,
        queryParams: QueryParams = {},
    ): Observable<ApiGetAllPaginatedResponse<any>> {
        let httpParams: HttpParams = new HttpParams();
        if (pagination.page) {
            httpParams = httpParams.set('page', pagination.page.toString());
        }

        httpParams = httpParams.set('page_size', pagination.limit ? pagination.limit.toString() : '10');

        for (const filterKey of Object.keys(filter)) {
            let filterValue = filter[filterKey];

            if (filterValue === undefined || filterValue === null || filterValue === '' || filterValue.length === 0) {
                continue;
            }
            filterValue = encodeURIComponent(filterValue);

            if (this.customFilters && this.customFilters.includes(filterKey)) {
                httpParams = httpParams.set(filterKey, filterValue);
            } else {
                httpParams = httpParams.set(`filter_by_${filterKey}`, filterValue);
            }
        }

        if (sort !== null) {
            httpParams = httpParams.set('sort_by', sort.field);
            httpParams = httpParams.set('sort_order', sort.type);
        }

        for (const paramName of Object.keys(queryParams)) {
            const paramValue = queryParams[paramName];
            httpParams = httpParams.set(paramName, paramValue);
        }

        return this.http
            .get<GetAllPaginatedResponse<ItemType>>(this.getLocation(), {
                params: httpParams,
            })
            .pipe(map(this.mapResponseToPaginator), catchError(this.convertToUserReadableError.bind(this)));
    }

    public getSingle(resourceId: string, queryParams: QueryParams = {}): Observable<ItemType> {
        let httpParams: HttpParams = new HttpParams();

        for (const paramName of Object.keys(queryParams)) {
            const paramValue = queryParams[paramName];
            httpParams = httpParams.set(paramName, paramValue);
        }

        return this.http
            .get<ItemType>(this.getLocation(resourceId), {
                params: httpParams,
            })
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public deleteSingle(resourceId: string): Observable<object> {
        return this.http.delete(this.getLocation(resourceId)).pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public deleteMultiple(resourceId: string, options: any): Observable<object> {
        return this.http.delete(this.getLocation(resourceId), options).pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public createSingle(formData: FormData, resourceId?: string, suffixResourceName?: string): Observable<ApiCreateSingleResponse> {
        let suffix = '';
        if (suffixResourceName) {
            suffix = `/${suffixResourceName}`;
        }

        return this.http
            .post<ApiCreateSingleResponse>(this.getLocation(resourceId) + suffix, formData)
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public updateSingle(resourceId: string, formData: FormData): Observable<object> {
        return this.http.put(this.getLocation(resourceId), formData).pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    public updateStatus(resourceId: string): Observable<object> {
        const formData = new FormData();
        formData.append('platform', 'mdr');

        return this.http
            .post<ApiCreateSingleResponse>(this.getLocation(resourceId), formData)
            .pipe(catchError(this.convertToUserReadableError.bind(this)));
    }

    protected mapResponseToPaginator(response: GetAllPaginatedResponse<ItemType>): ApiGetAllPaginatedResponse<ItemType> {
        return {
            page: response.page,
            pageCount: response.page_count,
            totalItems: response.total_items,
            items: response.items,
        } as ApiGetAllPaginatedResponse<ItemType>;
    }

    protected getLocation(resourceId?: string): string {
        if (resourceId) {
            return `${this.versionPrefix}/${this.resourceName}/${resourceId}`;
        }

        return `${this.versionPrefix}/${this.resourceName}`;
    }
}
